import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import FilterPanel from '../../components/ui/FilterPanel';
import FilterActions from '../../components/ui/FilterActions';
import SalesCallModal from '../../components/modals/SalesCallModal';
import MailingModal from '../../components/modals/MailingModal';
import { salesApiService } from '../../api/salesApi';
import { mailingApiService } from '../../api/mailingApi';
import { usersAPI } from '../../api/api';
import SalesDashboard from './SalesDashboard';
import SalesCallCalendar from './SalesCallCalendar';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PrinterIcon,
  ChartBarIcon,
  ListBulletIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowsUpDownIcon,
  DocumentDuplicateIcon,
  EnvelopeIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import SalesCallImportModal from './SalesCallImportModal';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import SalesCallPrintSheet from '../../components/salescall/SalesCallPrintSheet';
import ResponsiveModal from '../../components/ui/ResponsiveModal';
import IconActionButton from '../../components/ui/IconActionButton';
import ActionToolbar from '../../components/ui/ActionToolbar';
import { useAuth } from '../../contexts/AuthContext';
import {
  fetchAllSalesCallsMatchingFilters,
  MAX_SALES_CALL_PRINT_RECORDS,
  getLocalTodayCreatedAtIsoRange,
  MAX_EMAIL_PDF_TODAY_BATCH,
} from '../../utils/salesCallExport';

const INITIAL_SALES_CALL_FILTERS = {
  start_date: '',
  end_date: '',
  agency: '',
  agency_contact: '',
  phone: '',
  type: '',
  country: '',
  city: '',
  hotel: '',
  sales_representative: '',
  communication_type: '',
  call_status: '',
  call_result: '',
};

const DEFAULT_SALES_CALL_SORT = [{ id: 'created_at', desc: true }];

/** Maps TanStack sorting state to DRF `ordering` query (whitelist matches backend). */
function sortStateToOrdering(sorting) {
  if (!sorting?.length) return '-created_at';
  const { id, desc } = sorting[0];
  const fieldByColumnId = {
    hotel: 'hotel__hotel_name',
  };
  const field = fieldByColumnId[id] || id;
  return desc ? `-${field}` : field;
}

// DatePicker Component (StopSaleDashboard'dan alındı)
const DatePicker = ({ value, onChange, placeholder, name }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(value || '');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const generateCalendar = () => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const handleDateSelect = (day) => {
    if (day) {
      const formattedDate = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      setSelectedDate(formattedDate);
      onChange({ target: { value: formattedDate } });
      setIsOpen(false);
    }
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="relative" ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        value={formatDisplayDate(selectedDate)}
        onChange={() => { }}
        onClick={() => setIsOpen(!isOpen)}
        placeholder={placeholder}
        name={name}
        readOnly
        className="w-full px-1.5 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer"
      />
      {isOpen && (
        <div className="absolute z-50 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 w-64">
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={handlePrevMonth}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              ←
            </button>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {monthNames[currentMonth]} {currentYear}
            </span>
            <button
              onClick={handleNextMonth}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              →
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
              <div key={day} className="p-0.5 text-xs text-center text-gray-500 dark:text-gray-400 font-medium">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {generateCalendar().map((day, index) => (
              <button
                key={index}
                onClick={() => handleDateSelect(day)}
                disabled={!day}
                className={`
                  p-0.5 text-xs rounded transition-colors
                  ${!day ? 'invisible' : ''}
                  ${day ? 'hover:bg-blue-100 dark:hover:bg-blue-900/30 text-gray-900 dark:text-white' : ''}
                  ${selectedDate && parseInt(selectedDate.split('-')[2]) === day ? 'bg-blue-600 text-white' : ''}
                `}
              >
                {day}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const statusMap = {
  'Planlandı': 'Planned',
  'Tamamlandı': 'Completed',
  'İptal Edildi': 'Cancelled',
  'Ertelendi': 'Postponed',
  'Yüz Yüze': 'Face to Face',
  'Telefon': 'Phone',
  'Video Konferans': 'Video Conference',
  'E-posta': 'Email',
  'Başarılı': 'Successful',
  'Başarısız': 'Unsuccessful',
  'Beklemede': 'Pending',
  'Takip Gerekli': 'Follow-up Required'
};

const getStatusLabel = (status) => statusMap[status] || status;

/** StandardResponse may merge serializer dict at root. */
const unwrapSalesCallResponse = (res) => {
  if (!res?.success) return null;
  if (res.data && typeof res.data === 'object' && !Array.isArray(res.data)) return res.data;
  const { success, message, timestamp, errors, pagination: _p, data: _d, ...rest } = res;
  return rest.id != null ? rest : null;
};

const SalesCallPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const getInitialViewMode = (pathname) => {
    if (pathname.includes('/dashboard')) return 'dashboard';
    if (pathname.includes('/calendar')) return 'calendar';
    return 'list';
  };
  const [viewMode, setViewMode] = useState(getInitialViewMode(location.pathname));

  useEffect(() => {
    setViewMode(getInitialViewMode(location.pathname));
  }, [location.pathname]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [salesCalls, setSalesCalls] = useState([]);
  const [mailingCalendarItems, setMailingCalendarItems] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedMailing, setSelectedMailing] = useState(null);
  const [isMailingModalOpen, setIsMailingModalOpen] = useState(false);
  const [mailingReferenceData, setMailingReferenceData] = useState({
    mailingGoals: [],
    targetMarkets: [],
    users: []
  });
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [printCalls, setPrintCalls] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);
  const [isEmailTodayModalOpen, setIsEmailTodayModalOpen] = useState(false);
  const [emailTodayTo, setEmailTodayTo] = useState('');
  const [emailTodayName, setEmailTodayName] = useState('');
  const [emailTodaySending, setEmailTodaySending] = useState(false);
  const [emailTodayError, setEmailTodayError] = useState(null);
  const [emailTodaySuccess, setEmailTodaySuccess] = useState(null);
  const [filters, setFilters] = useState(() => ({ ...INITIAL_SALES_CALL_FILTERS }));
  const [sorting, setSorting] = useState(DEFAULT_SALES_CALL_SORT);
  const orderingParam = useMemo(() => sortStateToOrdering(sorting), [sorting]);
  const [filterOptionsData, setFilterOptionsData] = useState(null);

  const FIXED_TYPE_OPTIONS = [
    'Sales Call', 'Workshop', 'Roadshow', 'Fair', 'Webinar', 'AKKA Event',
    'Info Group (Stay)', 'Info Group (Inspection)', 'Whatsapp (Feedback)', 'CRM (Feedback by Mail)',
  ];
  const FIXED_COMMUNICATION_TYPE = [
    { value: 'Yüz Yüze', label: 'Face to Face' },
    { value: 'Telefon', label: 'Phone' },
    { value: 'Video Konferans', label: 'Video Conference' },
    { value: 'E-posta', label: 'Email' },
  ];
  const FIXED_CALL_STATUS = [
    { value: 'Planlandı', label: 'Planned' },
    { value: 'Tamamlandı', label: 'Completed' },
    { value: 'İptal Edildi', label: 'Cancelled' },
    { value: 'Ertelendi', label: 'Postponed' },
  ];
  const FIXED_CALL_RESULT = [
    { value: 'Başarılı', label: 'Successful' },
    { value: 'Başarısız', label: 'Unsuccessful' },
    { value: 'Beklemede', label: 'Pending' },
    { value: 'Takip Gerekli', label: 'Follow-up Required' },
  ];

  const filterOptions = useMemo(() => {
    const api = filterOptionsData || {};
    const toSelect = (arr, selectedValue = '') => {
      const vals = Array.isArray(arr) ? arr : [];
      const seen = new Set();
      const options = vals
        .filter((v) => v && !seen.has(v) && seen.add(v))
        .map((v) => ({ value: v, label: v }));
      const selected = String(selectedValue || '').trim();
      if (selected && !seen.has(selected)) {
        options.unshift({ value: selected, label: selected });
      }
      return options;
    };
    const mergeType = (apiArr, fixed) => {
      const fromApi = new Set(Array.isArray(apiArr) ? apiArr : []);
      const combined = [...fixed].filter((v) => v);
      fromApi.forEach((v) => { if (v && !fixed.includes(v)) combined.push(v); });
      return [...new Set(combined)].sort((a, b) => String(a).localeCompare(String(b))).map((v) => ({ value: v, label: v }));
    };
    const mergeFixed = (apiArr, fixedOptions) => {
      const fromApi = (Array.isArray(apiArr) ? apiArr : []).map((v) => ({ value: v, label: v }));
      const fixedValues = new Set(fixedOptions.map((o) => o.value));
      const extra = fromApi.filter((o) => o.value && !fixedValues.has(o.value));
      return [...fixedOptions, ...extra];
    };
    return {
      country: toSelect(api.country, filters.country),
      city: toSelect(api.city, filters.city),
      hotel: toSelect(api.hotel, filters.hotel),
      agency: toSelect(api.agency, filters.agency),
      agency_contact: toSelect(api.agency_contact, filters.agency_contact),
      sales_representative: toSelect(api.sales_representative, filters.sales_representative),
      type: mergeType(api.type, FIXED_TYPE_OPTIONS),
      communication_type: mergeFixed(api.communication_type, FIXED_COMMUNICATION_TYPE),
      call_status: mergeFixed(api.call_status, FIXED_CALL_STATUS),
      call_result: mergeFixed(api.call_result, FIXED_CALL_RESULT),
    };
  }, [filterOptionsData, filters]);

  const selectStyles = {
    control: (base) => ({ ...base, minHeight: '28px' }),
    menu: (base) => ({ ...base, zIndex: 50 }),
  };

  const selectClassNames = {
    control: () => '!min-h-[28px] !border-gray-300 dark:!border-gray-600 !rounded-md !bg-white dark:!bg-gray-700 !text-[11px]',
    singleValue: () => '!text-gray-900 dark:!text-gray-100',
    input: () => '!text-gray-900 dark:!text-gray-100',
    placeholder: () => '!text-gray-400',
    menu: () => '!z-50 !bg-white dark:!bg-gray-800 !border !border-gray-200 dark:!border-gray-700',
    option: (state) => `cursor-pointer ${state.isFocused ? '!bg-blue-50 dark:!bg-gray-700' : ''} ${state.isSelected ? '!bg-blue-100 dark:!bg-blue-900/50' : ''}`,
  };

  const loadFilterOptions = async (filterSnapshot) => {
    try {
      const response = await salesApiService.getSalesCallFilterOptions(filterSnapshot);
      if (!response?.success) return;
      const options =
        (response.data?.data && typeof response.data.data === 'object' ? response.data.data : null) ||
        (response.data && typeof response.data === 'object' && !Array.isArray(response.data)
          ? response.data
          : null) || {
          country: response.country,
          city: response.city,
          hotel: response.hotel,
          agency: response.agency,
          agency_contact: response.agency_contact,
          sales_representative: response.sales_representative,
          type: response.type,
          communication_type: response.communication_type,
          call_status: response.call_status,
          call_result: response.call_result,
        };
      if (options && (options.country || options.city || options.agency || options.type)) {
        setFilterOptionsData(options);
      }
    } catch (err) {
      console.error('Filter options load failed:', err);
    }
  };

  useEffect(() => {
    loadFilterOptions({ ...INITIAL_SALES_CALL_FILTERS });
  }, []);

  const isMailingRoadPlanner = location.pathname.includes('/sales-call/calendar');

  useEffect(() => {
    const fetchMailingCalendar = async () => {
      if (!isMailingRoadPlanner) return;
      try {
        setLoading(true);
        const response = await mailingApiService.getMailings();
        const mailings = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
        const mapped = mailings
          .filter((mailing) => mailing?.planned_date)
          .map((mailing) => ({
            id: `mailing-${mailing.id}`,
            start_date: `${mailing.planned_date}T09:00:00`,
            call_status: mailing.general_status || 'pending',
            agency: mailing.mailing_name || 'Mailing Planı',
            city: mailing.target_market || '',
            country: mailing.b2b_b2c ? mailing.b2b_b2c.toUpperCase() : '',
            originalMailing: mailing,
          }));
        setMailingCalendarItems(mapped);
      } catch (err) {
        setError('Mailing planları yüklenemedi.');
      } finally {
        setLoading(false);
      }
    };

    fetchMailingCalendar();
  }, [isMailingRoadPlanner]);

  useEffect(() => {
    const loadMailingReferenceData = async () => {
      if (!isMailingRoadPlanner) return;
      try {
        const usersResponse = await usersAPI.getUsers();
        const usersList = usersResponse?.success
          ? (usersResponse.data || []).map((u) => ({
              ...u,
              name: u.full_name || u.email,
            }))
          : [];

        const demoGoals = [
          { id: 1, value: 'kutlama', label: 'kutlama' },
          { id: 2, value: 'özel günün kutlaması', label: 'özel günün kutlaması' },
          { id: 3, value: 'duyuru', label: 'duyuru' },
          { id: 4, value: 'Increase and promote sales', label: 'Increase and promote sales' },
        ];

        const demoMarkets = [
          { id: 1, value: 'Tüm Pazarlar', label: 'Tüm Pazarlar' },
          { id: 2, value: 'BDT', label: 'BDT' },
          { id: 3, value: 'Balkan+Baltık', label: 'Balkan+Baltık' },
          { id: 4, value: 'Müslüman ülkeler', label: 'Müslüman ülkeler' },
          { id: 5, value: 'Rus pazarı', label: 'Rus pazarı' },
          { id: 6, value: 'Türkiye pazarı', label: 'Türkiye pazarı' },
        ];

        setMailingReferenceData({
          mailingGoals: demoGoals,
          targetMarkets: demoMarkets,
          users: usersList,
        });
      } catch (err) {
        // Silently handle reference load failures for modal fallback
      }
    };

    loadMailingReferenceData();
  }, [isMailingRoadPlanner]);

  const fetchSalesCalls = async (page = 1, filtersOverride = null) => {
    setLoading(true);
    setCurrentPage(page);
    try {
      const activeFilters = filtersOverride ?? filters;
      const response = await salesApiService.getSalesCalls({
        ...activeFilters,
        page,
        ordering: orderingParam,
      });
      if (response.success) {
        setSalesCalls(response.data || []);

        if (response.pagination) {
          setPagination(response.pagination);
        } else {
          setPagination(null);
        }
      } else {
        setError(response.message || 'Error loading data..');
      }
    } catch (err) {
      setError('Could not connect to server.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesCalls(1);
    // Intentionally only when server-side sort (ordering) changes; list filters are applied via Search / Clear.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderingParam]);

  const handleFilterChange = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    // Dropdown seçenekleri Search beklemeden güncellensin (rep kendi filtresiyle daralmasın).
    loadFilterOptions({ ...next, sales_representative: '' });
  };

  const clearFilters = () => {
    const cleared = { ...INITIAL_SALES_CALL_FILTERS };
    setFilters(cleared);
    loadFilterOptions(cleared);
    fetchSalesCalls(1, cleared);
  };

  const handleSearch = () => {
    // Rep filter must not narrow its own dropdown options after Search.
    loadFilterOptions({ ...filters, sales_representative: '' });
    fetchSalesCalls(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0) {
      fetchSalesCalls(newPage);
    }
  };

  const handleSaveMailingFromCalendar = async (data) => {
    try {
      setLoading(true);
      const response = selectedMailing?.id
        ? await mailingApiService.updateMailing(selectedMailing.id, data)
        : await mailingApiService.createMailing(data);

      if (response?.success) {
        setIsMailingModalOpen(false);
        setSelectedMailing(null);
        if (isMailingRoadPlanner) {
          const refresh = await mailingApiService.getMailings();
          const mailings = Array.isArray(refresh?.data) ? refresh.data : Array.isArray(refresh) ? refresh : [];
          const mapped = mailings
            .filter((mailing) => mailing?.planned_date)
            .map((mailing) => ({
              id: `mailing-${mailing.id}`,
              start_date: `${mailing.planned_date}T09:00:00`,
              call_status: mailing.general_status || 'pending',
              agency: mailing.mailing_name || 'Mailing Planı',
              city: mailing.target_market || '',
              country: mailing.b2b_b2c ? mailing.b2b_b2c.toUpperCase() : '',
              originalMailing: mailing,
            }));
          setMailingCalendarItems(mapped);
        }
      } else {
        setError(response?.message || 'Mailing güncellenemedi.');
      }
    } catch (err) {
      setError('Mailing kaydı sırasında hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSalesCall = () => {
    setSelectedEntry(null);
    setIsModalOpen(true);
  };

  const handleEditSalesCall = (entry) => {
    const formattedEntry = {
      ...entry,
      start_date: entry.start_date ? entry.start_date.replace('T', ' ').substring(0, 16) : '',
      end_date: entry.end_date ? entry.end_date.replace('T', ' ').substring(0, 16) : '',
    };
    setSelectedEntry(formattedEntry);
    setIsModalOpen(true);
  };

  const handleDeleteSalesCall = async (id) => {
    if (!window.confirm("Bu kaydı silmek istediğinize emin misiniz?")) return;
    setLoading(true);
    try {
      const response = await salesApiService.deleteSalesCall(id);
      if (response.success) {
        fetchSalesCalls(currentPage);
      } else {
        alert(response.message || 'Silme işlemi başarısız.');
      }
    } catch (e) {
      console.error(e);
      alert('Sunucu hatası.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSalesCall = async (formData, selectedFiles) => {
    setLoading(true);
    try {
      let response;
      let salesCallId;

      if (selectedEntry) {
        response = await salesApiService.updateSalesCall(selectedEntry.id, formData);
        salesCallId = selectedEntry.id;
      } else {
        response = await salesApiService.createSalesCall(formData);
        if (response.success) {
          salesCallId = response.id || (response.data && response.data.id);
        }
      }

      if (response.success) {
        if (selectedFiles && selectedFiles.length > 0 && salesCallId) {
          const fileData = new FormData();
          selectedFiles.forEach(file => {
            fileData.append('files', file);
          });
          await salesApiService.uploadSalesCallAttachments(salesCallId, fileData);
        }

        setIsModalOpen(false);
        setSelectedEntry(null);
        fetchSalesCalls(currentPage);
      } else {
        setError(response.message || 'Error occurred during operation.');
      }
    } catch (err) {
      console.error(err);
      setError('Server error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const handleImportSalesCalls = async (file) => {
    setIsImporting(true);
    setImportResult(null);
    try {
      const response = await salesApiService.importSalesCalls(file);
      if (response.success) {
        setImportResult(response.data);
        fetchSalesCalls(1);
      } else {
        setImportResult({
          created: 0,
          updated: 0,
          errors: [{ row: 0, error: response.message || 'Aktarım başarısız' }]
        });
      }
    } catch (err) {
      console.error(err);
      setImportResult({
        created: 0,
        updated: 0,
        errors: [{ row: 0, error: 'Sunucu hatası' }]
      });
    } finally {
      setIsImporting(false);
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      const response = await salesApiService.deleteSalesCallAttachment(attachmentId);
      if (response.success) {
        if (selectedEntry) {
          setSelectedEntry(prev => ({
            ...prev,
            attachments: prev.attachments.filter(a => a.id !== attachmentId)
          }));
        }
        fetchSalesCalls(currentPage);
      } else {
        alert(response.message || 'Failed to delete file.');
      }
    } catch (err) {
      console.error(err);
      alert('Error deleting file.');
    }
  };

  const handlePrintFilteredPdf = async () => {
    if (viewMode !== 'list') return;
    setPrintLoading(true);
    setError(null);
    try {
      const { calls, truncated, totalCount } = await fetchAllSalesCallsMatchingFilters(filters, {
        maxRecords: MAX_SALES_CALL_PRINT_RECORDS,
      });
      if (calls.length === 0) {
        setError('No records match the current filters.');
        return;
      }
      if (truncated) {
        window.alert(
          `Only the first ${calls.length} of ${totalCount} records will be included (limit ${MAX_SALES_CALL_PRINT_RECORDS}). Adjust filters or export in batches.`
        );
      }
      setPrintCalls(calls);
    } catch (e) {
      setError(e?.message || 'Could not load records for print.');
    } finally {
      setPrintLoading(false);
    }
  };

  const handlePrintSelected = async () => {
    if (!selectedEntry?.id) return;
    setPrintLoading(true);
    setError(null);
    try {
      const res = await salesApiService.getSalesCall(selectedEntry.id);
      const call = unwrapSalesCallResponse(res);
      if (!call) {
        setError('Could not load the selected record for print.');
        return;
      }
      setPrintCalls([call]);
    } catch (e) {
      setError(e?.message || 'Could not load record for print.');
    } finally {
      setPrintLoading(false);
    }
  };

  const handlePrintDone = () => {
    setPrintCalls(null);
  };

  const openEmailTodayModal = () => {
    setEmailTodayError(null);
    setEmailTodaySuccess(null);
    setEmailTodayTo((user?.email || '').trim());
    const displayName =
      (user?.full_name && String(user.full_name).trim()) ||
      [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
      '';
    setEmailTodayName(displayName);
    setIsEmailTodayModalOpen(true);
  };

  useEffect(() => {
    if (!isEmailTodayModalOpen) return undefined;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isEmailTodayModalOpen]);

  const handleSubmitEmailTodayPdfs = async (e) => {
    e.preventDefault();
    const to = emailTodayTo.trim();
    if (!to) {
      setEmailTodayError('E-posta adresi gerekli.');
      return;
    }
    setEmailTodayError(null);
    setEmailTodaySending(true);
    try {
      const range = getLocalTodayCreatedAtIsoRange();
      const { calls, truncated, totalCount } = await fetchAllSalesCallsMatchingFilters(
        { ...filters, ...range },
        { maxRecords: MAX_EMAIL_PDF_TODAY_BATCH }
      );
      if (calls.length === 0) {
        setEmailTodayError(
          'Bugün oluşturulan ve mevcut filtrelere uyan kayıt yok. Filtreleri gevşetip tekrar deneyin.'
        );
        return;
      }
      const name = emailTodayName.trim() || to.split('@')[0];
      const callIds = calls.map((c) => c.id);
      const res = await salesApiService.emailSalesCallPdfBatch({
        call_ids: callIds,
        to_email: to,
        to_name: name,
      });
      if (!res?.success) {
        setEmailTodayError(res?.message || 'Kuyruk oluşturulamadı.');
        return;
      }
      let msg = `Tek PDF raporu e-posta kuyruğuna alındı (${callIds.length} kayıt). email_daemon çalışıyorsa bir e-posta gönderilir.`;
      if (truncated && totalCount > calls.length) {
        msg += ` Toplam ${totalCount} kayıt vardı; güvenlik için ilk ${MAX_EMAIL_PDF_TODAY_BATCH} işlendi.`;
      }
      setEmailTodaySuccess(msg);
      setIsEmailTodayModalOpen(false);
    } catch (err) {
      setEmailTodayError(err?.message || 'İşlem başarısız.');
    } finally {
      setEmailTodaySending(false);
    }
  };

  const salesCallColumns = useMemo(
    () => [
      { accessorKey: 'agency', header: 'Agency', cell: ({ getValue }) => <span className="font-medium text-gray-900 dark:text-white">{getValue() || '-'}</span> },
      { accessorKey: 'agency_contact', header: 'Contact', cell: ({ getValue }) => <span className="text-gray-600 dark:text-gray-300">{getValue() || '-'}</span> },
      { accessorKey: 'phone', header: 'Phone', cell: ({ getValue }) => <span className="text-gray-600 dark:text-gray-300">{getValue() || '-'}</span> },
      { accessorKey: 'sales_representative', header: 'Representative', cell: ({ getValue }) => <span className="text-gray-600 dark:text-gray-300">{getValue() || '-'}</span> },
      { accessorKey: 'type', header: 'Type', cell: ({ getValue }) => <span className="text-gray-600 dark:text-gray-300">{getValue() || '-'}</span> },
      {
        id: 'country_city',
        enableSorting: false,
        header: 'Country/City',
        cell: ({ row }) => (
          <span className="text-gray-600 dark:text-gray-300">
            {[row.original.country, row.original.city].filter(Boolean).join(row.original.country && row.original.city ? '/' : '') || '-'}
          </span>
        ),
      },
      {
        id: 'hotel',
        header: 'Hotel',
        cell: ({ row }) => <span className="text-gray-600 dark:text-gray-300">{row.original.hotel || row.original.hotel_name || '-'}</span>,
      },
      { accessorKey: 'pax', header: 'Pax', cell: ({ getValue }) => <span className="text-gray-600 dark:text-gray-300">{getValue() || '0'}</span> },
      {
        accessorKey: 'communication_type',
        header: 'Comm. Type',
        cell: ({ getValue }) => <span className="text-gray-600 dark:text-gray-300">{getStatusLabel(getValue()) || '-'}</span>,
      },
      {
        accessorKey: 'start_date',
        header: 'Start',
        cell: ({ getValue }) => (
          <span className="text-gray-600 dark:text-gray-300">
            {getValue() ? new Date(getValue()).toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
          </span>
        ),
      },
      {
        accessorKey: 'end_date',
        header: 'End',
        cell: ({ getValue }) => (
          <span className="text-gray-600 dark:text-gray-300">
            {getValue() ? new Date(getValue()).toLocaleString('en-GB', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-'}
          </span>
        ),
      },
      {
        accessorKey: 'call_status',
        header: 'Status',
        cell: ({ getValue }) => (
          <span
            className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
              getValue() === 'Tamamlandı' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                getValue() === 'Planlandı' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' :
                  'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
            }`}
          >
            {getStatusLabel(getValue())}
          </span>
        ),
      },
      {
        accessorKey: 'call_result',
        header: 'Result',
        cell: ({ getValue }) => <span className="text-gray-600 dark:text-gray-300">{getStatusLabel(getValue()) || '-'}</span>,
      },
      {
        id: 'actions',
        enableSorting: false,
        header: () => <span className="text-right block w-full">Actions</span>,
        cell: ({ row }) => (
          <div className="flex justify-end space-x-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEditSalesCall(row.original);
              }}
              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
              title="Edit"
            >
              Edit
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteSalesCall(row.original.id);
              }}
              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 font-medium"
              title="Delete"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const salesCallTable = useReactTable({
    data: salesCalls,
    columns: salesCallColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
  });

  const SortIcon = ({ column }) => {
    const sorted = column.getIsSorted();
    if (sorted === 'asc') return <ArrowUpIcon className="w-3 h-3 text-blue-600" />;
    if (sorted === 'desc') return <ArrowDownIcon className="w-3 h-3 text-blue-600" />;
    return <ArrowsUpDownIcon className="w-3 h-3 text-gray-300 group-hover:text-gray-500 transition-colors" />;
  };

  return (
    <div className="space-y-6">
      <SalesCallModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveSalesCall}
        onDeleteAttachment={handleDeleteAttachment}
        initialData={selectedEntry}
        availableCalls={salesCalls}
      />

      <MailingModal
        isOpen={isMailingModalOpen}
        onClose={() => {
          setIsMailingModalOpen(false);
          setSelectedMailing(null);
        }}
        onSave={handleSaveMailingFromCalendar}
        initialData={selectedMailing}
        referenceData={mailingReferenceData}
      />

      <SalesCallImportModal
        isOpen={isImportModalOpen}
        onClose={() => {
          setIsImportModalOpen(false);
          setImportResult(null);
        }}
        onImport={handleImportSalesCalls}
        importing={isImporting}
        importResult={importResult}
        onDownloadTemplate={() => {
          try {
            salesApiService.downloadSalesCallTemplate();
          } catch (err) {
            console.error(err);
            alert('Şablon indirilemedi. Lütfen tekrar deneyin.');
          }
        }}
        onDownloadSablon2={() => {
          try {
            salesApiService.downloadSablon2Template();
          } catch (err) {
            console.error(err);
            alert('Şablon 2 indirilemedi. Lütfen tekrar deneyin.');
          }
        }}
      />

      {printCalls?.length ? (
        <SalesCallPrintSheet calls={printCalls} onAfterPrint={handlePrintDone} />
      ) : null}

      <div className="flex items-center justify-between pt-4">
        <div className="px-6">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {viewMode === 'dashboard' ? 'Sales Dashboard' :
              viewMode === 'calendar' ? (isMailingRoadPlanner ? 'Monthly Mailing Calendar' : 'Road Planner') :
                'B2B Meetings & Activities'}
          </h1>
        </div>
      </div>

      {error && (
        <div className="px-6">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
            {error}
            <button
              onClick={() => setError(null)}
              className="ml-2 text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {emailTodaySuccess && (
        <div className="px-6">
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded dark:bg-green-900/20 dark:border-green-800 dark:text-green-300 flex items-start justify-between gap-2">
            <span>{emailTodaySuccess}</span>
            <button
              type="button"
              onClick={() => setEmailTodaySuccess(null)}
              className="shrink-0 text-green-600 hover:text-green-800 dark:text-green-400"
              aria-label="Kapat"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="px-6">
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
            Loading data...
          </div>
        </div>
      )}

      {printLoading && (
        <div className="px-6">
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300">
            Preparing print…
          </div>
        </div>
      )}

      <div className="px-6">
        <ActionToolbar className="flex-wrap gap-1">
          <Button
            onClick={handleCreateSalesCall}
            variant="ghost"
            size="icon"
            className="text-gray-500 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400"
            title="New Record"
          >
            <PlusIcon className="w-4 h-4" />
          </Button>
          <Button
            onClick={() => setIsImportModalOpen(true)}
            variant="ghost"
            size="icon"
            className="text-gray-500 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400"
            title="Import Excel"
          >
            <ArrowDownTrayIcon className="w-4 h-4 rotate-180" />
          </Button>
          <IconActionButton
            icon={EnvelopeIcon}
            onClick={openEmailTodayModal}
            disabled={viewMode !== 'list' || loading || printLoading || emailTodaySending}
            title="Bugün oluşturulan kayıtları (filtreler + bugünün tarihi) seçilen e-postaya PDF ek ile kuyruğa al"
          />
          <Button
            onClick={handlePrintFilteredPdf}
            disabled={viewMode !== 'list' || loading || printLoading}
            variant="ghost"
            size="icon"
            className="text-gray-500 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 disabled:text-gray-300 dark:disabled:text-gray-600"
            title="Print all matching records (one per page). In the print dialog, turn off Headers and footers to remove URL, title, date, and page numbers at the edges."
          >
            <DocumentDuplicateIcon className="w-4 h-4" />
          </Button>
          <Button
            onClick={handlePrintSelected}
            disabled={!selectedEntry?.id || printLoading}
            variant="ghost"
            size="icon"
            className="text-gray-500 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400 disabled:text-gray-300 dark:disabled:text-gray-600"
            title="Print selected record (one page). In the print dialog, turn off Headers and footers to remove browser header/footer text."
          >
            <PrinterIcon className="w-4 h-4" />
          </Button>
        </ActionToolbar>
      </div>

      <ResponsiveModal isOpen={isEmailTodayModalOpen} onClose={() => !emailTodaySending && setIsEmailTodayModalOpen(false)}>
        <div className="px-4 pt-5 pb-4 sm:p-6 max-h-[88vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white">Bugünkü kayıtları PDF ile e-postala</h2>
            <button
              type="button"
              onClick={() => !emailTodaySending && setIsEmailTodayModalOpen(false)}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              aria-label="Kapat"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            <strong>Bugün</strong> (tarayıcı saatinize göre) oluşturulan ve alttaki listedeki <strong>mevcut filtrelerle</strong> eşleşen tüm kayıtlar <strong>tek bir PDF</strong> içinde birleştirilir; <strong>tek bir e-posta</strong> kuyruğa eklenir. En fazla {MAX_EMAIL_PDF_TODAY_BATCH} kayıt işlenir.
          </p>
          {emailTodayError && (
            <div className="mb-3 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded px-3 py-2">
              {emailTodayError}
            </div>
          )}
          <form onSubmit={handleSubmitEmailTodayPdfs} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="email-today-to" className="text-xs font-medium text-gray-700 dark:text-gray-300">
                Alıcı e-posta
              </Label>
              <Input
                id="email-today-to"
                type="email"
                value={emailTodayTo}
                onChange={(e) => setEmailTodayTo(e.target.value)}
                className="w-full"
                required
                autoComplete="email"
                disabled={emailTodaySending}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="email-today-name" className="text-xs font-medium text-gray-700 dark:text-gray-300">
                Alıcı adı (isteğe bağlı)
              </Label>
              <Input
                id="email-today-name"
                type="text"
                value={emailTodayName}
                onChange={(e) => setEmailTodayName(e.target.value)}
                className="w-full"
                disabled={emailTodaySending}
              />
            </div>
            <div className="mt-6 flex items-center justify-end space-x-3">
              <Button type="button" variant="outline" onClick={() => !emailTodaySending && setIsEmailTodayModalOpen(false)}>
                İptal
              </Button>
              <Button
                type="submit"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={emailTodaySending}
              >
                {emailTodaySending ? 'Kuyruğa alınıyor…' : 'Kuyruğa al'}
              </Button>
            </div>
          </form>
        </div>
      </ResponsiveModal>

      <div className="px-6">
        <FilterPanel
          title="Filters"
          isOpen={isFiltersOpen}
          onToggle={() => setIsFiltersOpen(!isFiltersOpen)}
        >
          <div className="mt-2 grid grid-cols-1 xl:grid-cols-2 gap-2">
            <div className="border border-gray-200 dark:border-gray-700 rounded-md p-2">
              <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-2">Tarih & Lokasyon</p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-1.5">
                <fieldset className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700">
                  <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">Start Date</legend>
                  <input type="date" value={filters.start_date} onChange={(e) => handleFilterChange('start_date', e.target.value)} className="w-full bg-transparent text-[11px] text-gray-900 dark:text-gray-100 outline-none" />
                </fieldset>
                <fieldset className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700">
                  <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">End Date</legend>
                  <input type="date" value={filters.end_date} onChange={(e) => handleFilterChange('end_date', e.target.value)} className="w-full bg-transparent text-[11px] text-gray-900 dark:text-gray-100 outline-none" />
                </fieldset>
                <fieldset className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700">
                  <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">Country</legend>
                  <CreatableSelect
                    isClearable
                    isSearchable
                    options={filterOptions.country}
                    value={filters.country ? { value: filters.country, label: filters.country } : null}
                    onChange={(s) => handleFilterChange('country', s?.value ?? '')}
                    onCreateOption={(v) => handleFilterChange('country', v)}
                    placeholder="Seçiniz..."
                    styles={selectStyles}
                    classNamePrefix="react-select"
                    classNames={selectClassNames}
                  />
                </fieldset>
                <fieldset className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700">
                  <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">City</legend>
                  <CreatableSelect
                    isClearable
                    isSearchable
                    options={filterOptions.city}
                    value={filters.city ? { value: filters.city, label: filters.city } : null}
                    onChange={(s) => handleFilterChange('city', s?.value ?? '')}
                    onCreateOption={(v) => handleFilterChange('city', v)}
                    placeholder="Seçiniz..."
                    styles={selectStyles}
                    classNamePrefix="react-select"
                    classNames={selectClassNames}
                  />
                </fieldset>
                <fieldset className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700">
                  <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">Hotel</legend>
                  <CreatableSelect
                    isClearable
                    isSearchable
                    options={filterOptions.hotel}
                    value={filters.hotel ? { value: filters.hotel, label: filters.hotel } : null}
                    onChange={(s) => handleFilterChange('hotel', s?.value ?? '')}
                    onCreateOption={(v) => handleFilterChange('hotel', v)}
                    placeholder="Seçiniz..."
                    styles={selectStyles}
                    classNamePrefix="react-select"
                    classNames={selectClassNames}
                  />
                </fieldset>
              </div>
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-md p-2">
              <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-2">Ajans & İletişim</p>
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-1.5">
                <fieldset className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700">
                  <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">Agency</legend>
                  <CreatableSelect
                    isClearable
                    isSearchable
                    options={filterOptions.agency}
                    value={filters.agency ? { value: filters.agency, label: filters.agency } : null}
                    onChange={(s) => handleFilterChange('agency', s?.value ?? '')}
                    onCreateOption={(v) => handleFilterChange('agency', v)}
                    placeholder="Seçiniz..."
                    styles={selectStyles}
                    classNamePrefix="react-select"
                    classNames={selectClassNames}
                  />
                </fieldset>
                <fieldset className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700">
                  <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">Contact</legend>
                  <CreatableSelect
                    isClearable
                    isSearchable
                    options={filterOptions.agency_contact}
                    value={filters.agency_contact ? { value: filters.agency_contact, label: filters.agency_contact } : null}
                    onChange={(s) => handleFilterChange('agency_contact', s?.value ?? '')}
                    onCreateOption={(v) => handleFilterChange('agency_contact', v)}
                    placeholder="Seçiniz..."
                    styles={selectStyles}
                    classNamePrefix="react-select"
                    classNames={selectClassNames}
                  />
                </fieldset>
                <fieldset className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700">
                  <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">Sales Representative</legend>
                  <CreatableSelect
                    isClearable
                    isSearchable
                    options={filterOptions.sales_representative}
                    value={filters.sales_representative ? { value: filters.sales_representative, label: filters.sales_representative } : null}
                    onChange={(s) => handleFilterChange('sales_representative', s?.value ?? '')}
                    onCreateOption={(v) => handleFilterChange('sales_representative', v)}
                    placeholder="Seçiniz..."
                    styles={selectStyles}
                    classNamePrefix="react-select"
                    classNames={selectClassNames}
                  />
                </fieldset>
                <fieldset className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700">
                  <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">Type</legend>
                  <CreatableSelect
                    isClearable
                    isSearchable
                    options={filterOptions.type}
                    value={filterOptions.type.find((o) => o.value === filters.type) || null}
                    onChange={(s) => handleFilterChange('type', s?.value ?? '')}
                    onCreateOption={(v) => handleFilterChange('type', v)}
                    placeholder="Seçiniz..."
                    styles={selectStyles}
                    classNamePrefix="react-select"
                    classNames={selectClassNames}
                  />
                </fieldset>
              </div>
            </div>

            <div className="border border-gray-200 dark:border-gray-700 rounded-md p-2 xl:col-span-2">
              <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-300 mb-2">Durum & Sonuç</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                <fieldset className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700">
                  <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">Communication Type</legend>
                  <Select
                    isClearable
                    isSearchable
                    options={filterOptions.communication_type}
                    value={filterOptions.communication_type.find((o) => o.value === filters.communication_type) || null}
                    onChange={(s) => handleFilterChange('communication_type', s?.value ?? '')}
                    placeholder="Seçiniz..."
                    styles={selectStyles}
                    classNamePrefix="react-select"
                    classNames={selectClassNames}
                  />
                </fieldset>
                <fieldset className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700">
                  <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">Status</legend>
                  <Select
                    isClearable
                    isSearchable
                    options={filterOptions.call_status}
                    value={filterOptions.call_status.find((o) => o.value === filters.call_status) || null}
                    onChange={(s) => handleFilterChange('call_status', s?.value ?? '')}
                    placeholder="Seçiniz..."
                    styles={selectStyles}
                    classNamePrefix="react-select"
                    classNames={selectClassNames}
                  />
                </fieldset>
                <fieldset className="border border-gray-300 dark:border-gray-600 rounded-md px-2 pb-1 pt-0.5 bg-white dark:bg-gray-700">
                  <legend className="px-1 text-[10px] text-gray-500 dark:text-gray-400 leading-none">Result</legend>
                  <Select
                    isClearable
                    isSearchable
                    options={filterOptions.call_result}
                    value={filterOptions.call_result.find((o) => o.value === filters.call_result) || null}
                    onChange={(s) => handleFilterChange('call_result', s?.value ?? '')}
                    placeholder="Seçiniz..."
                    styles={selectStyles}
                    classNamePrefix="react-select"
                    classNames={selectClassNames}
                  />
                </fieldset>
              </div>
            </div>

            <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
              <FilterActions
                onClear={clearFilters}
                onSearch={handleSearch}
                clearLabel="Clear"
                searchLabel="Search"
              />
            </div>
          </div>
        </FilterPanel>
      </div>

      <div className="px-6 pb-6">
        {viewMode === 'list' ? (
          <Card className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-4">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-900/50">
                    {salesCallTable.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className={`px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${
                              header.id === 'actions' ? 'text-right' : ''
                            } ${header.column.getCanSort() ? 'cursor-pointer select-none group hover:bg-gray-100 dark:hover:bg-gray-800' : ''}`}
                            onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                          >
                            <div className={`flex items-center gap-1.5 ${header.id === 'actions' ? 'justify-end' : ''}`}>
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {header.column.getCanSort() && header.id !== 'actions' ? (
                                <SortIcon column={header.column} />
                              ) : null}
                            </div>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {salesCallTable.getRowModel().rows.length === 0 ? (
                      <tr>
                        <td colSpan={salesCallTable.getAllColumns().length} className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                          No records found.
                        </td>
                      </tr>
                    ) : (
                      salesCallTable.getRowModel().rows.map((row) => (
                        <tr
                          key={row.id}
                          onClick={() => handleEditSalesCall(row.original)}
                          className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 ${selectedEntry?.id === row.original.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              className={`px-3 py-4 whitespace-nowrap text-xs ${cell.column.id === 'actions' ? 'text-right' : ''}`}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {pagination && (
                <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 px-4 py-3 sm:px-6 mt-4">
                  <div className="flex flex-1 justify-between sm:hidden">
                    <Button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={!pagination.previous}
                      variant="outline"
                      className="relative inline-flex items-center px-4 py-2 text-sm font-medium"
                    >
                      Previous
                    </Button>
                    <Button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={!pagination.next}
                      variant="outline"
                      className="relative ml-3 inline-flex items-center px-4 py-2 text-sm font-medium"
                    >
                      Next
                    </Button>
                  </div>
                  <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Showing <span className="font-medium">{(currentPage - 1) * pagination.page_size + 1}</span> to <span className="font-medium">{Math.min(currentPage * pagination.page_size, pagination.count)}</span> of{' '}
                        <span className="font-medium">{pagination.count}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={!pagination.previous}
                          className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Previous</span>
                          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                        </button>

                        {/* Sayfa Numaraları Basitleştirilmiş: Sadece current page gösteriyoruz şimdilik, karmaşık logic yerine */}
                        <button
                          aria-current="page"
                          className="relative z-10 inline-flex items-center bg-blue-600 px-4 py-2 text-sm font-semibold text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                        >
                          {currentPage}
                        </button>

                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={!pagination.next}
                          className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <span className="sr-only">Next</span>
                          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </Card>
        ) : viewMode === 'calendar' ? (
          <SalesCallCalendar
            salesCalls={isMailingRoadPlanner ? mailingCalendarItems : salesCalls}
            title={isMailingRoadPlanner ? 'Monthly Mailing Calendar' : 'Road Planner Calendar'}
            subtitle={
              isMailingRoadPlanner
                ? 'Mailing planlarını takvim üzerinde görüntüleyin ve düzenleyin.'
                : 'Ziyaret planlarını ve geçmiş görüşmeleri takvim üzerinde görüntüleyin.'
            }
            itemLabel={isMailingRoadPlanner ? 'Mailing' : 'Ziyaret'}
            showHeaderText={!isMailingRoadPlanner}
            onCallClick={(call) => {
              if (isMailingRoadPlanner) {
                setSelectedMailing(call?.originalMailing || null);
                setIsMailingModalOpen(true);
                return;
              }
              setSelectedEntry(call);
              setIsModalOpen(true);
            }}
            onDateSelect={(start, end) => {
              if (isMailingRoadPlanner) return;
              const year = start.getFullYear();
              const month = String(start.getMonth() + 1).padStart(2, '0');
              const day = String(start.getDate()).padStart(2, '0');
              const startDateStr = `${year}-${month}-${day} 09:00`;
              const endYear = end.getFullYear();
              const endMonth = String(end.getMonth() + 1).padStart(2, '0');
              const endDay = String(end.getDate()).padStart(2, '0');
              const endDateStr = `${endYear}-${endMonth}-${endDay} 18:00`;

              setSelectedEntry({
                start_date: startDateStr,
                end_date: endDateStr,
              });
              setIsModalOpen(true);
            }}
          />
        ) : (
          <SalesDashboard filters={filters} />
        )}
      </div>
    </div>
  );
};

export default SalesCallPage;
